import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Bot, User as UserIcon, ArrowRight } from 'lucide-react';
import { aiAPI } from '../../services/api';
import { useCart } from '../../context/CartContext';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const MOODS = ['Romantic', 'Confident', 'Relaxed', 'Fresh', 'Mysterious', 'Energetic'];
const OCCASIONS = ['Date Night', 'Office', 'Casual', 'Wedding', 'Evening Out', 'Everyday'];
const BUDGETS = ['$100', '$250', '$500', 'No Limit'];

const AIAdvisorPage = () => {
  const { addToCart } = useCart();
  const [mode, setMode] = useState('advisor'); // advisor | chat
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ mood: '', occasion: '', preferences: '', budget: '', gender: 'any' });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: "Welcome to Lumière's AI Fragrance Advisor! ✨ I'm here to guide you to your perfect scent. Ask me anything about perfumes, notes, occasions, or let me know your preferences." }]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleGetSuggestions = async () => {
    setLoading(true);
    try {
      const { data } = await aiAPI.suggest({ ...form, budget: form.budget === 'No Limit' ? '' : form.budget?.replace('$', '') });
      setResults(data);
      setStep(3);
    } catch { toast.error('Failed to get suggestions. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const userMsg = { role: 'user', content: chatInput };
    const history = [...messages, userMsg];
    setMessages(history);
    setChatInput('');
    setChatLoading(true);
    try {
      const { data } = await aiAPI.chat({ message: chatInput, history: messages.slice(-6) });
      setMessages([...history, { role: 'assistant', content: data.reply }]);
    } catch { setMessages([...history, { role: 'assistant', content: 'I apologize, I seem to be having trouble connecting. Please try again in a moment.' }]); }
    finally { setChatLoading(false); }
  };

  const StarScore = ({ score }) => (
    <div style={{ display: 'flex', gap: '2px' }}>
      {[...Array(10)].map((_, i) => (
        <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i < score ? 'var(--gold)' : 'var(--dark-3)' }} />
      ))}
    </div>
  );

  return (
    <div style={{ paddingTop: '72px', minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, var(--dark) 0%, var(--dark-2) 100%)', padding: '60px 0', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
        <div className="container">
          <Sparkles size={40} color="var(--gold)" style={{ marginBottom: '20px' }} />
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 60px)', fontWeight: 400, marginBottom: '16px' }}>
            AI Fragrance <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Advisor</span>
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--white-dim)', maxWidth: '500px', margin: '0 auto 32px', lineHeight: 1.8 }}>
            Discover your signature scent with our AI-powered recommendation engine.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0' }}>
            {[['advisor', '✨ Scent Advisor'], ['chat', '💬 Chat with AI']].map(([id, label]) => (
              <button key={id} onClick={() => setMode(id)}
                style={{ padding: '12px 28px', background: mode === id ? 'var(--gold)' : 'transparent', color: mode === id ? 'var(--black)' : 'var(--white-dim)', border: '1px solid var(--border)', fontSize: '12px', fontWeight: mode === id ? 700 : 400, letterSpacing: '0.1em', cursor: 'pointer', transition: 'all 0.3s' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '48px', paddingBottom: '80px' }}>
        {mode === 'advisor' && (
          <>
            {step < 3 && (
              <div style={{ maxWidth: '680px', margin: '0 auto' }}>
                {/* Progress */}
                <div style={{ display: 'flex', gap: '0', marginBottom: '40px' }}>
                  {['Your Mood', 'Preferences', 'Results'].map((s, i) => (
                    <div key={s} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: step > i ? 'var(--gold)' : step === i + 1 ? 'var(--gold)' : 'var(--dark-3)', border: `2px solid ${step >= i + 1 ? 'var(--gold)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, color: step >= i + 1 ? 'var(--black)' : 'var(--white-dim)', marginBottom: '6px' }}>
                          {i + 1}
                        </div>
                        <span style={{ fontSize: '10px', color: step === i + 1 ? 'var(--gold)' : 'var(--white-dim)', letterSpacing: '0.1em' }}>{s.toUpperCase()}</span>
                      </div>
                      {i < 2 && <div style={{ height: '1px', flex: 1, background: step > i + 1 ? 'var(--gold)' : 'var(--border)', marginTop: '-16px' }} />}
                    </div>
                  ))}
                </div>

                {step === 1 && (
                  <div>
                    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', marginBottom: '8px' }}>What's Your Mood?</h2>
                    <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginBottom: '28px' }}>Choose the feeling you want your fragrance to evoke</p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '32px' }}>
                      {MOODS.map(m => (
                        <button key={m} onClick={() => setForm(f => ({ ...f, mood: m }))}
                          style={{ padding: '16px', border: `1px solid ${form.mood === m ? 'var(--gold)' : 'var(--border)'}`, background: form.mood === m ? 'rgba(201,168,76,0.08)' : 'var(--dark-2)', color: form.mood === m ? 'var(--gold)' : 'var(--white)', fontSize: '13px', cursor: 'pointer', transition: 'all 0.3s' }}>
                          {m}
                        </button>
                      ))}
                    </div>

                    <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Occasion</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '32px' }}>
                      {OCCASIONS.map(o => (
                        <button key={o} onClick={() => setForm(f => ({ ...f, occasion: o }))}
                          style={{ padding: '12px', border: `1px solid ${form.occasion === o ? 'var(--gold)' : 'var(--border)'}`, background: form.occasion === o ? 'rgba(201,168,76,0.08)' : 'var(--dark-2)', color: form.occasion === o ? 'var(--gold)' : 'var(--white)', fontSize: '12px', cursor: 'pointer', transition: 'all 0.3s' }}>
                          {o}
                        </button>
                      ))}
                    </div>

                    <button onClick={() => setStep(2)} disabled={!form.mood}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', background: form.mood ? 'var(--gold)' : 'var(--dark-3)', color: form.mood ? 'var(--black)' : 'var(--white-dim)', padding: '14px 32px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: form.mood ? 'pointer' : 'not-allowed', transition: 'all 0.3s' }}>
                      NEXT <ArrowRight size={14} />
                    </button>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', marginBottom: '8px' }}>Your Preferences</h2>
                    <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginBottom: '28px' }}>Help us refine your perfect match</p>

                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', display: 'block', marginBottom: '10px' }}>GENDER PREFERENCE</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {['any', 'men', 'women', 'unisex'].map(g => (
                          <button key={g} onClick={() => setForm(f => ({ ...f, gender: g }))}
                            style={{ padding: '8px 16px', border: `1px solid ${form.gender === g ? 'var(--gold)' : 'var(--border)'}`, background: form.gender === g ? 'var(--gold)' : 'transparent', color: form.gender === g ? 'var(--black)' : 'var(--white)', fontSize: '12px', cursor: 'pointer', textTransform: 'capitalize', transition: 'all 0.3s' }}>
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', display: 'block', marginBottom: '10px' }}>BUDGET</label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {BUDGETS.map(b => (
                          <button key={b} onClick={() => setForm(f => ({ ...f, budget: b }))}
                            style={{ padding: '8px 16px', border: `1px solid ${form.budget === b ? 'var(--gold)' : 'var(--border)'}`, background: form.budget === b ? 'var(--gold)' : 'transparent', color: form.budget === b ? 'var(--black)' : 'var(--white)', fontSize: '12px', cursor: 'pointer', transition: 'all 0.3s' }}>
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: '32px' }}>
                      <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', display: 'block', marginBottom: '10px' }}>DESCRIBE YOUR SCENT PREFERENCES</label>
                      <textarea value={form.preferences} onChange={e => setForm(f => ({ ...f, preferences: e.target.value }))}
                        placeholder="e.g., I love warm, spicy scents with woody base notes. I prefer something long-lasting..."
                        rows={4} style={{ width: '100%', padding: '14px', background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '13px', lineHeight: 1.7, resize: 'vertical' }} />
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button onClick={() => setStep(1)} style={{ padding: '14px 24px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--white)', fontSize: '11px', letterSpacing: '0.15em', cursor: 'pointer' }}>← BACK</button>
                      <button onClick={handleGetSuggestions} disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--gold)', color: 'var(--black)', padding: '14px 32px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1 }}>
                        {loading ? <><span className="animate-spin" style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid var(--black)', borderTopColor: 'transparent', borderRadius: '50%' }} /> ANALYZING...</> : <><Sparkles size={14} /> GET MY RECOMMENDATIONS</>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 3 && results && (
              <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                  <Sparkles size={32} color="var(--gold)" style={{ marginBottom: '16px' }} />
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--white-dim)', fontStyle: 'italic', maxWidth: '600px', margin: '0 auto', lineHeight: 1.8 }}>{results.advisorMessage}</p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '40px' }}>
                  {results.recommendations.map((rec, i) => (
                    <div key={i} style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '32px', display: 'grid', gridTemplateColumns: '120px 1fr', gap: '32px', alignItems: 'start' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '100px', height: '100px', background: 'var(--dark-3)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                          {rec.product?.images?.[0] && <img src={rec.product.images[0]} alt={rec.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: '10px', color: 'var(--white-dim)', marginBottom: '4px' }}>MATCH</p>
                          <StarScore score={rec.matchScore} />
                          <p style={{ fontSize: '13px', color: 'var(--gold)', fontWeight: 700, marginTop: '4px' }}>{rec.matchScore}/10</p>
                        </div>
                      </div>
                      <div>
                        <p style={{ fontSize: '11px', color: 'var(--gold)', letterSpacing: '0.2em', marginBottom: '6px' }}>{rec.brand?.toUpperCase()}</p>
                        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '26px', marginBottom: '12px' }}>{rec.name}</h3>
                        <p style={{ fontSize: '13px', color: 'var(--white-dim)', lineHeight: 1.8, marginBottom: '12px' }}>{rec.whyItMatches}</p>
                        <p style={{ fontSize: '12px', color: 'var(--white-faint)', marginBottom: '8px', fontStyle: 'italic' }}>{rec.scentJourney}</p>
                        <p style={{ fontSize: '12px', color: 'var(--white-dim)', marginBottom: '16px' }}>✨ {rec.bestFor}</p>
                        {rec.personalNote && <p style={{ fontSize: '12px', color: 'var(--gold)', fontStyle: 'italic', marginBottom: '20px' }}>"{rec.personalNote}"</p>}
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          {rec.product && (
                            <>
                              <span style={{ fontFamily: 'var(--font-display)', fontSize: '24px' }}>${rec.product.salePrice || rec.product.price}</span>
                              <button onClick={() => addToCart(rec.product._id, 1, rec.product.size?.[0])}
                                style={{ background: 'var(--gold)', color: 'var(--black)', padding: '10px 20px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', cursor: 'pointer', transition: 'background 0.3s' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--gold-light)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'var(--gold)'}
                              >ADD TO CART</button>
                              <Link to={`/product/${rec.product._id}`} style={{ fontSize: '12px', color: 'var(--white-dim)', letterSpacing: '0.1em', borderBottom: '1px solid var(--border)', paddingBottom: '2px' }}>VIEW DETAILS</Link>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <button onClick={() => { setStep(1); setResults(null); setForm({ mood: '', occasion: '', preferences: '', budget: '', gender: 'any' }); }}
                    style={{ border: '1px solid var(--border)', background: 'transparent', color: 'var(--white)', padding: '12px 24px', fontSize: '11px', letterSpacing: '0.15em', cursor: 'pointer' }}>
                    ← START OVER
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Chat Mode */}
        {mode === 'chat' && (
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', minHeight: '480px', maxHeight: '480px', overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '0' }}>
              {messages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: msg.role === 'assistant' ? 'rgba(201,168,76,0.15)' : 'var(--dark-3)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {msg.role === 'assistant' ? <Bot size={14} color="var(--gold)" /> : <UserIcon size={14} color="var(--white-dim)" />}
                  </div>
                  <div style={{ maxWidth: '80%', background: msg.role === 'assistant' ? 'var(--dark-3)' : 'rgba(201,168,76,0.1)', border: `1px solid ${msg.role === 'assistant' ? 'var(--border)' : 'rgba(201,168,76,0.2)'}`, padding: '12px 16px', fontSize: '13px', lineHeight: 1.8, color: 'var(--white)' }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(201,168,76,0.15)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Bot size={14} color="var(--gold)" />
                  </div>
                  <div style={{ background: 'var(--dark-3)', border: '1px solid var(--border)', padding: '12px 20px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {[0, 0.2, 0.4].map((delay, i) => (
                      <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--gold)', animation: `pulse 1.2s ease ${delay}s infinite` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <form onSubmit={handleChat} style={{ display: 'flex', gap: '0', borderTop: '1px solid var(--border)' }}>
              <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                placeholder="Ask about fragrances, notes, or your perfect scent..."
                style={{ flex: 1, padding: '16px', background: 'var(--dark-2)', border: 'none', borderLeft: '1px solid var(--border)', borderBottom: '1px solid var(--border)', color: 'var(--white)', fontSize: '13px', outline: 'none' }} />
              <button type="submit" disabled={chatLoading || !chatInput.trim()}
                style={{ padding: '0 20px', background: 'var(--gold)', border: 'none', color: 'var(--black)', cursor: chatLoading ? 'not-allowed' : 'pointer', transition: 'background 0.3s' }}
                onMouseEnter={e => { if (!chatLoading) e.currentTarget.style.background = 'var(--gold-light)'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--gold)'}
              ><Send size={16} /></button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAdvisorPage;
